import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import "./style/Dashboard.css";

import { AiOutlineMail } from "react-icons/ai";
import {
  MdLocalShipping,
  MdOutlinePersonOff,
  MdHelpOutline,
  MdPreview,
  MdScience,
  MdOutlineForum,
  MdOutlineCancelScheduleSend
} from "react-icons/md";
import { LuCalendarClock } from "react-icons/lu";
import MaterialTable from "material-table";
import { Form } from "react-bootstrap";
import { debounce } from "lodash";

import DashboardSingleCard from "./DashboardSingleCard";
import QuickActionSingle from "./QuickActionSingle";
import SwitchButton from "./SwitchButton";

import { getMessageCount, getBookingByDate, getKitByStatus, getOrderByStatus, getTestResultWithStatus } from "./helper/apiCaller";
import { BookingColumns, KIT_COLUMNS, TEST_RESULT_COLUMNS, ORDER_PENDING_SHIPMENT_COLUMNS } from "./helper/tableColoumns";
import { AVAILABLE_STATES_FULL } from "./helper/constant";
import { MultipleSelectAutocomplete } from "./MultipleSelectAutocomplete";
import { clinicianIdFromRefreshAuth } from "../../util/authUtil";
import ApiService from "../../ApiService";

const options = {
  paging: true
};

const FILTER_OPTIONS = [
  {
    value: "All",
    label: "All"
  },
  {
    value: "Online",
    label: "Online"
  },
  {
    value: "2101 Larimer St, Denver",
    label: "Denver Care Hub @ 2101 Larimer St, Denver"
  },
  {
    value: "8 E Charleston Blvd, Las Vegas",
    label: "Las Vegas Care Hub @ 8 E Charleston Blvd"
  }
];

export default function Dashboard() {
  let navigate = useHistory();
  const [activeCard, setActiveCard] = useState("todaysAppointment");
  const [tableState, setTableState] = useState({
    columns: BookingColumns,
    data: [],
    heading: "Today’s Appointment"
  });

  const [clinicianState, setClinicianState] = useState("");
  useEffect(() => {
    clinicianIdFromRefreshAuth().then(id => {
      ApiService.getClinicianProfile(id)
        .then(res => {
          if (res.status === 200) {
            setClinicianState(res.data?.state);
          }
        })
        .catch(err => {
          console.log(err);
        });
    });
  }, []);

  const [messageCount, setMessageCount] = useState(0);
  const [todaysBooking, setTodaysBooking] = useState([]);
  const [kitWithRejectedSamples, setKitWithRejectedSamples] = useState([]);
  const [orderPendingShipment, setOrderPendingShipment] = useState([]);
  const [testResultWithNoPatients, setTestResultWithNoPatients] = useState([]);
  const [testResultsWithInconclusiveResults, setTestResultsWithInconclusiveResults] = useState([]);
  const [testResultsNeedingReview, setTestResultsNeedingReview] = useState([]);
  const [testResultsPendingConsultation, setTestResultsPendingConsultation] = useState([]);
  const [labOrderPendingResults, setLabOrderPendingResults] = useState([]);

  const [filter, setFilter] = useState("All");
  const [stateFilter, setStateFilter] = useState([{ id: "All", title: "All" }]);
  const [switchStateFilter, setSwitchStateFilter] = useState("myStates");

  useEffect(() => {
    getMessageCount(res => {
      if (res.success) {
        setMessageCount(res.data);
      }
    });
    const date = new Date().toISOString().split("T")[0];
    getBookingByDate(date, res => {
      if (res.success) {
        setTodaysBooking(res.data);
        setTableState(prevState => ({
          ...prevState,
          data: res.data,
          heading: "Today’s Appointment"
        }));
      }
    });
    getKitByStatus("REJECTED", res => {
      if (res.success) {
        setKitWithRejectedSamples(res.data);
      }
    });
    getKitByStatus("PENDING_RESULTS", res => {
      if (res.success) {
        setLabOrderPendingResults(res.data);
      }
    });
    getOrderByStatus("PENDING_SHIPMENT", res => {
      if (res.success) {
        setOrderPendingShipment(res.data);
      }
    });
    getTestResultWithStatus("NO_PATIENT", res => {
      if (res.success) {
        setTestResultWithNoPatients(res.data);
      }
    });
    getTestResultWithStatus("INCONCLUSIVE", res => {
      if (res.success) {
        setTestResultsWithInconclusiveResults(res.data);
      }
    });
    getTestResultWithStatus("NEEDS_CLINICIAN_REVIEW", res => {
      if (res.success) {
        setTestResultsNeedingReview(res.data);
      }
    });
    getTestResultWithStatus("NEEDS_CONSULT", res => {
      if (res.success) {
        setTestResultsPendingConsultation(res.data);
      }
    });
  }, []);

  const cards = [
    {
      heading: "Unread Messages",
      type: "unreadMessages",
      count: messageCount,
      IconComponent: <AiOutlineMail />,
      isActive: activeCard === "unreadMessages"
    },

    {
      heading: "Today’s Appointment",
      type: "todaysAppointment",
      count: todaysBooking?.length,
      IconComponent: <LuCalendarClock />,
      isActive: activeCard === "todaysAppointment"
    },
    {
      heading: "Rejected Samples",
      type: "rejectedSamples",
      count: kitWithRejectedSamples?.length,
      IconComponent: <MdOutlineCancelScheduleSend />,
      isActive: activeCard === "rejectedSamples"
    },
    {
      heading: "Order Pending Shipment",
      type: "orderPendingShipment",
      count: orderPendingShipment?.length,
      IconComponent: <MdLocalShipping />,
      isActive: activeCard === "orderPendingShipment",
      isFocus: true
    },
    {
      heading: "Test results with no patients",
      type: "testResultsWithNoPatients",
      count: testResultWithNoPatients?.length,
      IconComponent: <MdOutlinePersonOff />,
      isActive: activeCard === "testResultsWithNoPatients",
      isFocus: true
    },
    {
      heading: "Test results with inconclusive results",
      type: "testResultsWithInconclusiveResults",
      count: testResultsWithInconclusiveResults?.length,
      IconComponent: <MdHelpOutline />,
      isActive: activeCard === "testResultsWithInconclusiveResults"
    },
    {
      heading: "Test results needing review",
      type: "testResultsNeedingReview",
      count: testResultsNeedingReview?.length,
      IconComponent: <MdPreview />,
      isActive: activeCard === "testResultsNeedingReview"
    },
    {
      heading: "Test results pending consultation",
      type: "testResultsPendingConsultation",
      count: testResultsPendingConsultation?.length,
      IconComponent: <MdOutlineForum />,
      isActive: activeCard === "testResultsPendingConsultation",
      isFocus: true
    },
    {
      heading: "Lab Order Pending Results",
      type: "labOrderPendingResults",
      count: labOrderPendingResults?.length,
      IconComponent: <MdScience />,
      isActive: activeCard === "labOrderPendingResults",
      isFocus: true
    }
  ];

  // Debounce setup for handling card changes to prevent rapid state updates
  const debouncedChangeCard = useCallback(
    debounce(newState => {
      setTableState(newState);
    }, 300),
    []
  );

  // Card click handler for changing cards
  const handleCardClick = useCallback(
    type => {
      if (activeCard === type) return;
      if (type === "unreadMessages") {
        navigate.push("/inbox");
      }
      let newState = {
        columns: BookingColumns,
        data: [],
        heading: ""
      };

      if (type === "todaysAppointment") {
        newState.data = todaysBooking;
        newState.columns = BookingColumns;
        newState.heading = "Today’s Appointment";
        setActiveCard("todaysAppointment");
      } else if (type === "rejectedSamples") {
        newState.data = kitWithRejectedSamples;
        newState.columns = KIT_COLUMNS;
        newState.heading = "Rejected Samples";
        setActiveCard("rejectedSamples");
      } else if (type === "orderPendingShipment") {
        newState.data = orderPendingShipment;
        newState.columns = ORDER_PENDING_SHIPMENT_COLUMNS;
        newState.heading = "Order Pending Shipment";
        setActiveCard("orderPendingShipment");
      } else if (type === "testResultsWithNoPatients") {
        newState.data = testResultWithNoPatients;
        newState.columns = TEST_RESULT_COLUMNS;
        newState.heading = "Test Results pending patient register";
        setActiveCard("testResultsWithNoPatients");
      } else if (type === "testResultsWithInconclusiveResults") {
        newState.data = testResultsWithInconclusiveResults;
        newState.columns = TEST_RESULT_COLUMNS;
        newState.heading = "Test Results With Inconclusive Results";
        setActiveCard("testResultsWithInconclusiveResults");
      } else if (type === "testResultsNeedingReview") {
        newState.data = testResultsNeedingReview;
        newState.columns = TEST_RESULT_COLUMNS;
        newState.heading = "Test Results Need Review";
        setActiveCard("testResultsNeedingReview");
      } else if (type === "testResultsPendingConsultation") {
        newState.data = testResultsPendingConsultation;
        newState.columns = TEST_RESULT_COLUMNS;
        newState.heading = "Test Results Pending Consultation";
        setActiveCard("testResultsPendingConsultation");
      } else if (type === "labOrderPendingResults") {
        newState.data = labOrderPendingResults;
        newState.columns = KIT_COLUMNS;
        newState.heading = "Lab Order Pending Results";
        setActiveCard("labOrderPendingResults");
      }
      debouncedChangeCard(newState);
    },
    [
      activeCard,
      debouncedChangeCard,
      todaysBooking,
      kitWithRejectedSamples,
      orderPendingShipment,
      testResultWithNoPatients,
      testResultsWithInconclusiveResults,
      testResultsNeedingReview,
      testResultsPendingConsultation,
      labOrderPendingResults
    ]
  );

  //booking -> careStateRegion
  //kit -> careLocation -> always online
  //testResult -> careLocation

  const handleRowClick = row => {
    if (activeCard === "todaysAppointment") {
      // open in new window
      window.open(`/BookingDetails?bookingId=${row.bookingId}`, '_blank');
    }
    if (activeCard === "rejectedSamples") {
      window.open(`KitDetails?kitId=${row.kitId}`, '_blank');
    }
    // if (activeCard === "orderPendingShipment") {
    //   window.open(`/OrderDetails?orderId=${row.orderId}`, '_blank');
    // }
    if (activeCard === "testResultsWithNoPatients") {
      window.open(`KitDetails?kitId=${row.kitId}`, '_blank');
    }
    if (activeCard === "testResultsWithInconclusiveResults") {
      window.open(`KitDetails?kitId=${row.kitId}`, '_blank');
    }
    if (activeCard === "testResultsNeedingReview") {
      window.open(`KitDetails?kitId=${row.kitId}`, '_blank');
    }
    if (activeCard === "testResultsPendingConsultation") {
      window.open(`KitDetails?kitId=${row.kitId}`, '_blank');
    }
    if (activeCard === "labOrderPendingResults") {
      window.open(`KitDetails?kitId=${row.kitId}`, '_blank');
    }
  };


  const filteredData = useMemo(() => {
    let data = tableState.data;

    // Apply the general 'filter' criteria
    if (filter === "Online") {
      data = data.filter(item => item?.careLocation === "Online");
    } else if (filter !== "All") {
      data = data.filter(item => item?.careLocation === filter);
    }

    // Apply the 'switchStateFilter' and 'stateFilter'
    if (switchStateFilter === "myState") {
      const filterProperty = activeCard === "todaysAppointment" ? "careStateRegion" : "careLocation";
      data = data.filter(item => item[filterProperty] === clinicianState);
    } else if (switchStateFilter === "allStates" && stateFilter.length > 0 && stateFilter[0].title !== "All") {
      const stateTitles = stateFilter.map(state => state.title);
      data = data.filter(item => stateTitles.includes(item?.careStateRegion));
    }

    return data;
  }, [filter, activeCard, clinicianState, stateFilter, switchStateFilter, tableState.data]);

  return (
    <div className="dashboard">
      <div className="section-1">
        <h1 className="heading mar-t-2">Overview</h1>
        <div>
          <div className="filter-section">
            <Form>
              <Form.Group controlId="exampleForm.ControlSelect1">
                {/* <Form.Label>Filter</Form.Label> */}
                <Form.Control
                  size="md"
                  as="select"
                  value={filter}
                  onChange={e => {
                    setFilter(e.target.value);
                  }}
                >
                  {FILTER_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Form>

            <SwitchButton setValue={setSwitchStateFilter} value={switchStateFilter} />
            {switchStateFilter !== "myStates" && (
              <MultipleSelectAutocomplete mapOptions={AVAILABLE_STATES_FULL} setValue={setStateFilter} value={stateFilter} />
            )}
          </div>
        </div>
      </div>
      <div className="content-container">
        <div className="section-2">
          <div className="dashboard-cards">
            {cards.map(card => (
              <DashboardSingleCard
                key={card.heading}
                heading={card.heading}
                count={card.count}
                IconComponent={card.IconComponent}
                isActive={activeCard === card.type}
                onClick={() => handleCardClick(card.type)}
                isAttentionRequired={card.isFocus && card.count > 0}
              />
            ))}
          </div>

          <div className="dashboard-table-cont">
            <div className={`dashboard-table`}>
              <div className="table-container">
                <h2 className="heading">{tableState.heading}</h2>
                <table className="table">
                  <thead>
                    <tr>
                      {tableState.columns.map(column => (
                        <th key={column.title}>{column.title}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                  {filteredData.map((row, index) => (
                      <tr key={index}>
                        {tableState.columns.map((column) => (
                            <td key={column.field}>
                              {column?.isLink ? (
                                  <span className="hyperlink" onClick={() => handleRowClick(row)}>{row[column.field]}</span>
                              ) : (
                                  row[column.field]
                              )}
                            </td>
                        ))}
                      </tr>
                  ))}
                  </tbody>

                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
